// Modal «Registrar movimiento»: entrada, merma o ajuste y traspaso, por pieza o por cantidad según el SKU.
import { useEffect, useId, useState } from 'react';
import { Controller, useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';
import { toast } from 'sonner';
import { ErrorState, Input, Modal, Skeleton, Tabs, Button } from '@/kit';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/cn';
import { useIdempotentAttempt } from '@/hooks/useIdempotentAttempt';
import type { InventoryLensPeriod } from '@/api/services/retail-catalog/backoffice';
import { useCatalogProduct } from '../../api/useCatalogWrites';
import { useInventorySku } from '../../api/useInventorySku';
import { useRefreshInventory, useRegisterMovement } from '../../api/useInventoryMovements';
import type { StoreDirectory } from '../../lib/inventoryDetail';
import { displayName, type InventoryRow } from '../../lib/inventoryList';
import {
    ADJUSTMENT_REASON_OPTIONS,
    EMPTY_MOVEMENT_FORM,
    MAX_MOVEMENT_PIECES,
    MOVEMENT_KIND_LABEL,
    availablePieces,
    failedIdentifierOf,
    movementFormSchema,
    parseIdentifierList,
    storeOptionsOf,
    toMovementRequest,
    tracksPieces,
    type MovementForm,
    type MovementKind,
} from '../../lib/inventoryMovements';
import { applyWriteError } from '../../lib/inventoryWriteErrors';
import { FormSelect, WriteErrorNotice } from '../catalog/FormBits';

export interface MovementContext {
    stores: StoreDirectory;
    period: InventoryLensPeriod;
    retailerId: string | null;
}

const NOTE: Record<MovementKind, string> = {
    ENTRY: 'Suma al saldo de la tienda con un movimiento de entrada. Por IMEI o serie, cada pieza queda disponible para vender.',
    ADJUSTMENT:
        'Resta del disponible de la tienda y queda en el libro con su motivo. Si sobra stock, registra una entrada.',
    TRANSFER:
        'Sale de la tienda origen y queda en tránsito hasta que la tienda destino lo reciba. Mientras tanto no se vende en ninguna de las dos.',
};

const SUCCESS: Record<MovementKind, string> = {
    ENTRY: 'Entrada registrada',
    ADJUSTMENT: 'Merma o ajuste registrado',
    TRANSFER: 'Traspaso enviado: queda en tránsito',
};

const PIECES_LABEL = 'text-sm font-medium text-foreground';

function stockLine(row: InventoryRow, storeId: string): string | undefined {
    if (storeId === '') return undefined;
    const stock = row.byStore.find((s) => s.storeId === storeId)?.stock ?? 0;
    return `Stock actual en la tienda: ${String(stock)}`;
}

/** Captura de IMEI o serie de una entrada; marca la pieza que el silo rechazó. */
function IdentifierInput({
    form,
    marked,
}: {
    form: UseFormReturn<MovementForm>;
    marked: string | null;
}) {
    const id = useId();
    const text = useWatch({ control: form.control, name: 'identifiersText' });
    const { identifiers, duplicates } = parseIdentifierList(text);
    const error = form.formState.errors.identifiersText?.message;
    return (
        <div className="flex flex-col gap-1">
            <label htmlFor={id} className={PIECES_LABEL}>
                IMEI o número de serie
            </label>
            <Textarea
                id={id}
                rows={4}
                aria-invalid={error ? true : undefined}
                placeholder="Uno por renglón"
                className="font-mono text-[12px]"
                {...form.register('identifiersText')}
            />
            <p className="text-[10.5px] text-muted-foreground">
                {`${String(identifiers.length)} de ${String(MAX_MOVEMENT_PIECES)} piezas por movimiento. La cantidad es el número de piezas.`}
            </p>
            {identifiers.length > 0 ? (
                <ul aria-label="Piezas capturadas" className="flex flex-wrap gap-1">
                    {identifiers.map((identifier) => {
                        const bad = identifier === marked || duplicates.includes(identifier);
                        return (
                            <li
                                key={identifier}
                                data-invalid={bad ? true : undefined}
                                className={cn(
                                    'rounded border px-1.5 py-px font-mono text-[10.5px]',
                                    bad
                                        ? 'border-danger bg-danger-soft text-danger'
                                        : 'border-border bg-muted/40',
                                )}
                            >
                                {identifier}
                                {bad ? <span className="sr-only"> (rechazada)</span> : null}
                            </li>
                        );
                    })}
                </ul>
            ) : null}
            {error ? <p className="text-[11px] text-destructive">{error}</p> : null}
        </div>
    );
}

/** Piezas disponibles de la tienda origen para una merma o un traspaso; tope de 30. */
function PiecePicker({
    form,
    sku,
    period,
    marked,
}: {
    form: UseFormReturn<MovementForm>;
    sku: string;
    period: InventoryLensPeriod;
    marked: string | null;
}) {
    const storeId = useWatch({ control: form.control, name: 'storeId' });
    const query = useInventorySku(storeId === '' ? null : sku, period, storeId || undefined);
    const pieces = availablePieces(query.data?.pages.at(0)?.piecesByStore ?? [], storeId);
    const error = form.formState.errors.picked?.message;
    const [dropped, setDropped] = useState<string[]>([]);
    const [seenKey, setSeenKey] = useState<string | null>(null);
    const piecesKey = query.data ? pieces.join('|') : null;
    const isGone = (identifier: string) => !pieces.includes(identifier);

    // Al recargar las piezas se sueltan las elegidas que ya no están, porque sin checkbox no se desmarcan.
    if (piecesKey !== seenKey) {
        setSeenKey(piecesKey);
        const gone = piecesKey === null ? [] : form.getValues('picked').filter(isGone);
        if (gone.length > 0) setDropped(gone);
    }
    useEffect(() => {
        if (piecesKey === null) return;
        const available = piecesKey === '' ? [] : piecesKey.split('|');
        const picked = form.getValues('picked');
        if (picked.every((p) => available.includes(p))) return;
        form.setValue(
            'picked',
            picked.filter((p) => available.includes(p)),
        );
    }, [piecesKey, form]);

    return (
        <fieldset className="flex flex-col gap-1">
            <legend className={cn(PIECES_LABEL, 'mb-1')}>Piezas disponibles</legend>
            {dropped.length > 0 ? (
                <p role="status" className="text-[11px] text-warning">
                    {dropped.length === 1
                        ? `Ya no está disponible y se quitó de la selección: ${dropped[0]}.`
                        : `Ya no están disponibles y se quitaron de la selección: ${dropped.join(', ')}.`}
                </p>
            ) : null}
            {storeId === '' ? (
                <p className="text-[11px] text-muted-foreground">Elige primero la tienda.</p>
            ) : query.isPending ? (
                <Skeleton lines={2} />
            ) : query.error ? (
                <p className="text-[11px] text-danger">
                    No se pudieron leer las piezas de la tienda.
                </p>
            ) : pieces.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                    Esta tienda no tiene piezas disponibles de este SKU.
                </p>
            ) : (
                <Controller
                    control={form.control}
                    name="picked"
                    render={({ field }) => (
                        <div className="grid max-h-40 grid-cols-2 gap-x-3 gap-y-1 overflow-y-auto rounded-md border border-border/60 p-2">
                            {pieces.map((identifier) => {
                                const checked = field.value.includes(identifier);
                                const full = field.value.length >= MAX_MOVEMENT_PIECES;
                                const rejected = identifier === marked;
                                return (
                                    <label
                                        key={identifier}
                                        className={cn(
                                            'inline-flex items-center gap-1.5 font-mono text-[11px]',
                                            rejected && 'text-danger',
                                        )}
                                    >
                                        <input
                                            type="checkbox"
                                            className="size-3.5 accent-primary"
                                            checked={checked}
                                            disabled={!checked && full}
                                            aria-invalid={rejected ? true : undefined}
                                            onChange={(event) => {
                                                field.onChange(
                                                    event.target.checked
                                                        ? [...field.value, identifier]
                                                        : field.value.filter(
                                                              (v) => v !== identifier,
                                                          ),
                                                );
                                            }}
                                        />
                                        {identifier}
                                        {rejected ? (
                                            <span className="sr-only"> (rechazada)</span>
                                        ) : null}
                                    </label>
                                );
                            })}
                        </div>
                    )}
                />
            )}
            <p className="text-[10.5px] text-muted-foreground">
                {`Hasta ${String(MAX_MOVEMENT_PIECES)} piezas por movimiento.`}
            </p>
            {error ? <p className="text-[11px] text-destructive">{error}</p> : null}
        </fieldset>
    );
}

function MovementFields({
    kind,
    row,
    ctx,
    withPieces,
    imeiTracked,
    initialStoreId,
    onDone,
    onCancel,
}: {
    kind: MovementKind;
    row: InventoryRow;
    ctx: MovementContext;
    withPieces: boolean;
    imeiTracked: boolean;
    initialStoreId: string;
    onDone: () => void;
    onCancel: () => void;
}) {
    const register = useRegisterMovement();
    const refresh = useRefreshInventory();
    const attempt = useIdempotentAttempt(`movement:${row.sku}`);
    const [error, setError] = useState<string | null>(null);
    const [marked, setMarked] = useState<string | null>(null);
    const form = useForm<MovementForm>({
        resolver: zodResolver(movementFormSchema(withPieces, imeiTracked)),
        defaultValues: { ...EMPTY_MOVEMENT_FORM, kind, storeId: initialStoreId },
    });
    const { errors } = form.formState;
    const storeOptions = storeOptionsOf(ctx.stores);
    const storeId = useWatch({ control: form.control, name: 'storeId' });
    const piecesField = kind === 'ENTRY' ? 'identifiersText' : 'picked';
    const countSurplusId = useId();

    const submit = async (values: MovementForm) => {
        const request = toMovementRequest(values, row.sku, withPieces);
        setError(null);
        setMarked(null);
        try {
            await attempt.run(request, (key) => register.mutateAsync({ request, key }));
            toast.success(SUCCESS[kind], { description: row.sku });
            onDone();
        } catch (failure) {
            const mapped = applyWriteError(failure, attempt, request);
            const piece = failedIdentifierOf(failure);
            setError(piece ? `${mapped.message} Pieza: ${piece}.` : mapped.message);
            if (piece) setMarked(piece);
            if (mapped.action === 'PICK_OTHER_STORE') {
                const field = mapped.code === 'SAME_STORE_TRANSFER' ? 'toStoreId' : 'storeId';
                form.setError(field, { message: mapped.message });
            }
            if (mapped.action === 'FIX_QUANTITY' || mapped.action === 'WAIT_RESERVATION') {
                form.setError(withPieces ? piecesField : 'qty', { message: mapped.message });
            }
            if (mapped.action === 'RELOAD') void refresh();
        }
    };

    return (
        <form
            noValidate
            aria-label={MOVEMENT_KIND_LABEL[kind]}
            className="flex flex-col gap-3"
            onSubmit={(event) => {
                void form.handleSubmit(submit)(event);
            }}
        >
            <div className="flex gap-2 rounded-md border border-info-soft-border bg-info-soft px-3 py-2 text-[11px] text-info">
                <Info className="mt-px size-3.5 flex-none" aria-hidden />
                <span>{NOTE[kind]}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Controller
                    control={form.control}
                    name="storeId"
                    render={({ field }) => (
                        <div>
                            <FormSelect
                                label={kind === 'TRANSFER' ? 'Tienda origen' : 'Tienda'}
                                value={field.value}
                                onChange={(next) => {
                                    field.onChange(next);
                                    form.setValue('picked', []);
                                }}
                                options={storeOptions}
                                placeholder="Elige la tienda"
                                error={errors.storeId?.message}
                                required
                            />
                            {stockLine(row, storeId) ? (
                                <p className="mt-1 text-[10.5px] text-muted-foreground">
                                    {stockLine(row, storeId)}
                                </p>
                            ) : null}
                        </div>
                    )}
                />
                {kind === 'TRANSFER' ? (
                    <Controller
                        control={form.control}
                        name="toStoreId"
                        render={({ field }) => (
                            <FormSelect
                                label="Tienda destino"
                                value={field.value}
                                onChange={field.onChange}
                                options={storeOptions.filter((o) => o.value !== storeId)}
                                placeholder="Elige la tienda"
                                error={errors.toStoreId?.message}
                                required
                            />
                        )}
                    />
                ) : null}
                {kind === 'ADJUSTMENT' ? (
                    <Controller
                        control={form.control}
                        name="reason"
                        render={({ field }) => (
                            <FormSelect
                                label="Motivo"
                                value={field.value}
                                onChange={field.onChange}
                                options={ADJUSTMENT_REASON_OPTIONS}
                                placeholder="Elige el motivo"
                                error={errors.reason?.message}
                                required
                            />
                        )}
                    />
                ) : null}
            </div>
            {withPieces && kind === 'ENTRY' ? (
                <IdentifierInput form={form} marked={marked} />
            ) : null}
            {withPieces && kind !== 'ENTRY' ? (
                <PiecePicker form={form} sku={row.sku} period={ctx.period} marked={marked} />
            ) : null}
            {withPieces ? null : (
                <Input
                    label="Cantidad"
                    required
                    inputMode="numeric"
                    hint="De 1 a 10 000 unidades."
                    error={errors.qty?.message}
                    {...form.register('qty')}
                />
            )}
            {kind === 'ENTRY' ? (
                <div className="flex items-start gap-2">
                    <Controller
                        control={form.control}
                        name="countSurplus"
                        render={({ field }) => (
                            <Checkbox
                                id={countSurplusId}
                                aria-describedby={`${countSurplusId}-hint`}
                                checked={field.value}
                                className="mt-0.5"
                                onCheckedChange={(checked) => {
                                    field.onChange(checked === true);
                                }}
                            />
                        )}
                    />
                    <div className="flex flex-col">
                        <label
                            htmlFor={countSurplusId}
                            className="text-sm leading-snug text-foreground"
                        >
                            Sobrante de conteo
                        </label>
                        <p
                            id={`${countSurplusId}-hint`}
                            className="text-[10.5px] text-muted-foreground"
                        >
                            Márcala si el stock apareció al contar la tienda y no llegó de un
                            proveedor.
                        </p>
                    </div>
                </div>
            ) : null}
            <WriteErrorNotice message={error} />
            <div className="flex justify-end gap-2 border-t border-border pt-3">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={register.isPending}
                >
                    Cancelar
                </Button>
                <Button type="submit" isLoading={register.isPending}>
                    Registrar
                </Button>
            </div>
        </form>
    );
}

export function RegisterMovementModal({
    isOpen,
    onClose,
    row,
    ctx,
    kinds,
    initialKind,
    initialStoreId = '',
}: {
    isOpen: boolean;
    onClose: () => void;
    row: InventoryRow;
    ctx: MovementContext;
    /** Tipos que el rol puede registrar. */
    kinds: readonly MovementKind[];
    initialKind: MovementKind;
    /** Tienda (origen en el traspaso) con la que arranca el formulario. */
    initialStoreId?: string;
}) {
    const [kind, setKind] = useState<MovementKind>(initialKind);
    const product = useCatalogProduct(ctx.retailerId, row.sku, isOpen);
    const withPieces = tracksPieces(product.data?.trackingMode);
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Registrar movimiento · ${displayName(row)}`}
            size="lg"
        >
            <Tabs
                tabs={kinds.map((k) => ({ key: k, label: MOVEMENT_KIND_LABEL[k] }))}
                activeTab={kind}
                onChange={(key) => {
                    setKind(key as MovementKind);
                }}
                className="mb-1"
            />
            {ctx.retailerId === null ? (
                <WriteErrorNotice message="Tu usuario no tiene una sola cadena asignada. Pide a tu administrador que te asigne la cadena que operas." />
            ) : product.isPending ? (
                <Skeleton lines={4} />
            ) : product.error ? (
                <ErrorState
                    title="No se pudo leer el producto"
                    description="Sin su rastreo no sabemos si pedir piezas o cantidad."
                    onRetry={() => {
                        void product.refetch();
                    }}
                />
            ) : (
                <MovementFields
                    key={`${kind}:${String(withPieces)}`}
                    kind={kind}
                    row={row}
                    ctx={ctx}
                    withPieces={withPieces}
                    imeiTracked={product.data?.trackingMode === 'IMEI'}
                    initialStoreId={initialStoreId}
                    onDone={onClose}
                    onCancel={onClose}
                />
            )}
        </Modal>
    );
}
